import { CloseOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';

import { AnalyzeWebsiteTrafficProps } from '@mm/state/modules/modalsState/props';
import { EmailInput } from '../../../../common/modals/InviteUserModal/EmailInput/EmailInput';
import styles from './AnalyzeWebsiteTraffic.module.css';
import { EMAIL_PLACEHOLDER } from './constants';
import animation from './search-animated.gif';
import loading from './homepage-loading.gif';
import howItWorks from 'assets/how-it-works.png';
import { useAnalyzeWebsiteTraffic } from './use-analyze-website-traffic';
import { Skeleton } from 'antd';
import { useCanResolveDomain } from '@mm/hooks';
import { trackWebsiteGAEvent } from '../../../../common/helpers/plugins/website-google-analytics';
import { GA_EVENTS } from '../../../../common/helpers/plugins/constants';
import TextInput from '../../../../common/components/TextInput';

const PHRASES = [
  'Analyzing your website traffic',
  'Checking your social media',
  'Evaluating your email capture rate',
  'Cleaning and preparing data',
  'Generating your report',
  'Delivering report',
];

export const AnalyzeWebsiteTraffic = (props: AnalyzeWebsiteTrafficProps) => {
  const [firstName, setFirstName] = useState('');
  const [companyName, setCompanyName] = useState('');

  const {
    emailState,
    onCloseClick,
    onEmailChange,
    onSubmit,
    showCloseByTimer,
    trafficLoaded,
  } = useAnalyzeWebsiteTraffic({ ...props, firstName, companyName });

  const { website } = props;

  const [phraseIndex, setPhraseIndex] = useState(0);

  const { canResolveDomain } = useCanResolveDomain({
    websiteURL: website,
    initialValue: true,
  });

  const auth = THUM_IO_AUTH_KEY ? `/auth/${THUM_IO_AUTH_KEY}` : '';
  const screenshotSrc = `https://image.thum.io/get${auth}/fit/304x216/https://${website}`;

  const [isImageErrored, setIsImageErrored] = useState(false);
  const [isImageValid, setIsImageValid] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const phrase = PHRASES[phraseIndex];

  useEffect(() => {
    const handle = setInterval(() => {
      setPhraseIndex(phraseIndex =>
        phraseIndex < PHRASES.length - 1 ? phraseIndex + 1 : phraseIndex,
      );
    }, 5000);
    return () => clearInterval(handle);
  }, []);

  return (
    <div className={styles.container}>
      {showCloseByTimer && trafficLoaded && (
        <CloseOutlined className={styles.closeIcon} onClick={onCloseClick} />
      )}

      <div className={styles.header}>
        <div className={styles.title}>
          Analyzing your traffic and generating your report
        </div>
        <div className={styles.website}>{website}</div>
      </div>

      <div className={styles.contentContainer}>
        <div className={styles.column1}>
          <div className={styles.info}>
            <div className={styles.animationContainer}>
              <img src={animation} className={styles.animation} />
            </div>
            <div className={styles.text}>{phrase}...</div>
          </div>
          <div className={styles.formContainer}>
            <div className={styles.instruction}>
              Fill out the form to continue:
            </div>
            <form
              onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
                trackWebsiteGAEvent(GA_EVENTS.submitLostLeadReportEmail.name, {
                  category: '',
                });
                onSubmit(e);
              }}
            >
              <div className={styles.form_input_group}>
                <label className={styles.form_label}>Email</label>
                <EmailInput
                  onChange={onEmailChange}
                  inputProps={{
                    placeholder: EMAIL_PLACEHOLDER,
                    className: styles.form_input_group,
                  }}
                  withIcon={false}
                  strict
                  defaultValue={emailState.email}
                />
              </div>

              <div className={styles.form_input_group}>
                <label className={styles.form_label}>First Name</label>

                <TextInput
                  className={styles.form_input}
                  onChange={e => setFirstName(e.target.value)}
                  aria-label="First name"
                  type="text"
                  value={firstName}
                />
              </div>

              <div className={styles.form_input_group}>
                <label className={styles.form_label}>Company Name</label>
                <TextInput
                  className={styles.form_input}
                  onChange={e => setCompanyName(e.target.value)}
                  aria-label="Company"
                  value={companyName}
                  type="text"
                />
              </div>

              <button
                disabled={!emailState.isValid}
                className={styles.submitButton}
              >
                Send my report
              </button>
            </form>
          </div>
        </div>
        <div className={styles.column2}>
          {!canResolveDomain ||
          isImageErrored ||
          (!isImageValid && !isLoading) ? (
            <img src={howItWorks} className={styles.browserContent} />
          ) : (
            <div className={styles.browserFrame}>
              <img
                style={{
                  display: isLoading ? 'none' : 'block',
                }}
                src={screenshotSrc}
                alt="screenshot"
                onLoad={() => {
                  setIsImageValid(true);
                  setIsLoading(false);
                }}
                onError={() => {
                  setIsImageErrored(true);
                  setIsLoading(false);
                }}
                className={styles.browserContent}
              />

              {isLoading && (
                <>
                  <Skeleton.Image
                    active={loading}
                    className={styles.skeletonImage}
                  />
                  <Skeleton
                    active={loading}
                    paragraph={{ rows: 2 }}
                    className={styles.skeletonRow}
                  />
                  <Skeleton.Image
                    active={loading}
                    className={styles.skeletonImage}
                  />
                  <img src={loading} className={styles.loadingAnimation} />
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
